import { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getAllApprovalTransactions, clearFlags, resetHistoryTransactions } from '../../store/slices/transactionApprovalSlice';
import { 
  Card, Typography, Tag, Row, Col, Divider,
  CustomButton, CustomSelect, CustomDatePicker,
  Colors, Constants, showToast, getDateFromDateString, removeUnderScoreAndCapitalize,
  Spin
} from '../../utils/commonImports';
import { EditOutlined } from '@ant-design/icons';
import UpdateApprovalStatusModal from '../../components/ui/modals/UpdateApprovalStatusModal';

const { Title, Text } = Typography;

const TransactionApprovals = () => {
  const dispatch = useDispatch();
  const { 
    pendingTransactions,
    historyTransactions,
    loading, 
    historyLoading,
    isUpdated, 
    error 
  } = useSelector((state) => state.transactionApproval);
  const { user } = useSelector((state) => state.auth);
  
  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [selectedApproval, setSelectedApproval] = useState(null);
  const [historyPage, setHistoryPage] = useState(1);
  const [hasMoreHistory, setHasMoreHistory] = useState(true);
  const [expandedDescriptions, setExpandedDescriptions] = useState({});
  const observerTarget = useRef(null);
  
  // Filter states for history section
  const historyLimit = 20;
  const pendingLimit = 100;
  const initialHistoryFilters = {
    status: '',
    startDate: null,
    endDate: null
  };
  const [historyFilters, setHistoryFilters] = useState(initialHistoryFilters);

  // Fetch pending transactions on mount
  useEffect(() => {
    dispatch(getAllApprovalTransactions({
      page: 1,
      perPage: pendingLimit,
      status: Constants.APPROVAL_STATUS.PENDING
    }));
  }, [dispatch]);

  // Fetch history transactions (approved + rejected)
  useEffect(() => {
    const fetchHistory = async () => {
      const filterParams = {
        page: historyPage,
        perPage: historyLimit,
        ...Object.fromEntries(Object.entries(historyFilters).filter(([_, v]) => v !== null && v !== ''))
      };
      
      if (historyFilters.startDate) filterParams.startDate = historyFilters.startDate.format('YYYY-MM-DD');
      if (historyFilters.endDate) filterParams.endDate = historyFilters.endDate.format('YYYY-MM-DD');
      
      // Use array of statuses if no specific status filter, otherwise use single status
      try {
        let statusParam;
        if (historyFilters.status) {
          // Single status filter
          statusParam = historyFilters.status;
        } else {
          // Fetch both approved and rejected using array
          statusParam = [Constants.APPROVAL_STATUS.APPROVED, Constants.APPROVAL_STATUS.REJECTED];
        }
        
        const result = await dispatch(getAllApprovalTransactions({ 
          ...filterParams, 
          status: statusParam 
        })).unwrap();
        
        const totalAvailable = result?.pagination?.totalApprovals || 0;
        // Calculate if more pages are available using total and current page size
        const loadedCount = ((historyPage - 1) * historyLimit) + ((result?.approvalTransactions || []).length);
        const hasMore = loadedCount < totalAvailable;
        setHasMoreHistory(hasMore);
      } catch (error) {
        console.error('Error fetching history:', error);
        setHasMoreHistory(false);
      }
    };
    
    fetchHistory();
  }, [dispatch, historyPage, historyFilters]);

  useEffect(() => {
    if (isUpdated) {
      dispatch(clearFlags());
      // Refresh pending transactions
      dispatch(getAllApprovalTransactions({
        page: 1,
        perPage: pendingLimit,
        status: Constants.APPROVAL_STATUS.PENDING
      }));
      // Reset and reload history
      setHistoryPage(1);
      dispatch(resetHistoryTransactions());
      setHasMoreHistory(true);
    }

    if (error) {
      showToast(error, 'error');
    }
  }, [isUpdated, dispatch, error]);

  // Infinite scroll observer
  useEffect(() => {
    const currentTarget = observerTarget.current;
    if (!currentTarget) return;

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && hasMoreHistory && !historyLoading) {
          setHistoryPage(prev => {
            const nextPage = prev + 1;
            return nextPage;
          });
        }
      },
      { threshold: 0.1, rootMargin: '200px' }
    );

    // Always observe the target, conditions are checked in the callback
    observer.observe(currentTarget);

    return () => {
      observer.unobserve(currentTarget);
    };
  }, [hasMoreHistory, historyLoading, historyPage]);

  // Reset history when filters change
  useEffect(() => {
    setHistoryPage(1);
    dispatch(resetHistoryTransactions());
    setHasMoreHistory(true);
  }, [dispatch, historyFilters]);

  const getApprovalStatusColor = (status) => {
    switch (status) {
      case Constants.APPROVAL_STATUS.PENDING:
        return Colors.warning;
      case Constants.APPROVAL_STATUS.APPROVED:
        return Colors.success;
      case Constants.APPROVAL_STATUS.REJECTED:
        return Colors.error;
      default:
        return Colors.gray;
    }
  };

  const getTransactionTypeColor = (type) => {
    return type === 'payment' ? Colors.primary : Colors.info;
  };

  const renderApprovalCard = (approval, index, showUpdateButton = true) => {
    const transaction = approval.transactionId;
    const createdBy = transaction?.userId;
    const approvedBy = approval.approvedBy;
    const transactionType = transaction?.transactionType || 'payment';
    const descriptionKey = approval._id || transaction?._id || `approval-${index}`;

    return (
      <Col xs={24} sm={12} lg={8} key={approval._id || index}>
        <Card
          style={{
            height: '100%',
            borderRadius: '12px',
            boxShadow: Colors.cardShadow,
            border: `1px solid ${Colors.lightGray}`,
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.boxShadow = Colors.cardShadowHover;
            e.currentTarget.style.transform = 'translateY(-2px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.boxShadow = Colors.cardShadow;
            e.currentTarget.style.transform = 'translateY(0)';
          }}
        >
          {/* Transaction Type and Approval Status in same row */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            gap: '8px', 
            marginBottom: '12px',
            flexWrap: 'wrap'
          }}>
            <div style={{ 
              display: 'inline-block',
              padding: '6px 16px',
              borderRadius: '20px',
              backgroundColor: getTransactionTypeColor(transactionType),
              color: Colors.white,
              fontSize: '11px',
              fontWeight: '700',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              {transactionType === 'payment' ? 'Transaction' : 'Installment'}
            </div>
            <Tag 
              color={getApprovalStatusColor(approval.approvalStatus)} 
              className="flashing-status"
              style={{ 
                textTransform: 'capitalize',
                fontSize: '11px',
                padding: '2px 8px',
                borderRadius: '8px',
                margin: 0,
                marginLeft: 'auto'
              }}
            >
              {approval.approvalStatus}
            </Tag>
          </div>

          {/* Status Tags */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
            {transaction?.accountType && (
              <Tag color={transaction.accountType === 'corporate' ? Colors.primary : Colors.info} style={{ 
                textTransform: 'capitalize',
                fontSize: '11px',
                padding: '2px 8px',
                margin: 0
              }}>
                {transaction.accountType}
              </Tag>
            )}
            {transaction?.businessStatus && (
              <Tag color={transaction.businessStatus === 'inhouse' ? Colors.primary : Colors.warning} style={{ 
                textTransform: 'capitalize',
                fontSize: '11px',
                padding: '2px 8px',
                margin: 0
              }}>
                {transaction.businessStatus}
              </Tag>
            )}
          </div>

          {/* Description with clamp & see more/less */}
          <div style={{ marginBottom: '8px' }}>
            <div
              style={{
                fontSize: '14px',
                color: Colors.textPrimary,
                lineHeight: '1.4',
                whiteSpace: 'pre-wrap',
                ...(expandedDescriptions[descriptionKey] ? {} : {
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden'
                })
              }}
            >
              {transaction?.description || 'No description'}
            </div>
            {transaction?.description && transaction.description.length > 120 && (
              <span
                onClick={() => setExpandedDescriptions(prev => ({ ...prev, [descriptionKey]: !prev[descriptionKey] }))}
                style={{ color: Colors.primary, cursor: 'pointer', fontSize: '11px', display: 'inline-block', marginTop: '4px' }}
              >
                {expandedDescriptions[descriptionKey] ? 'See less' : 'See more'}
              </span>
            )}
          </div>

          {/* Amount */}
          <Text style={{ 
            fontSize: '20px', 
            fontWeight: '700', 
            color: Colors.primary, 
            display: 'block', 
            marginBottom: '12px'
          }}>
            ₹{transaction?.total ? transaction.total.toFixed(2) : '0.00'}
          </Text>

          {/* Details Section */}
          <div style={{ 
            marginTop: '12px', 
            paddingTop: '12px', 
            borderTop: `1px solid ${Colors.lightGray}` 
          }}>
            <Row gutter={8} style={{ marginBottom: '8px' }}>
              <Col span={12}>
                <Text style={{ fontSize: '11px', color: Colors.gray, display: 'block', marginBottom: '2px' }}>Transaction Date</Text>
                <Text style={{ fontSize: '12px', fontWeight: '500' }}>
                  {transaction?.date ? getDateFromDateString(transaction.date) : '-'}
                </Text>
              </Col>
              <Col span={12}>
                <Text style={{ fontSize: '11px', color: Colors.gray, display: 'block', marginBottom: '2px' }}>Created By</Text>
                <Text style={{ fontSize: '12px', fontWeight: '500' }}>
                  {createdBy ? `${createdBy.firstName} ${createdBy.lastName || ''}` : '-'}
                </Text>
              </Col>
            </Row>
            {approvedBy && (
              <div>
                <Text style={{ fontSize: '11px', color: Colors.gray, display: 'block', marginBottom: '2px' }}>Approved By</Text>
                <Text style={{ fontSize: '12px', fontWeight: '500' }}>
                  {approvedBy.firstName} {approvedBy.lastName || ''}
                </Text>
              </div>
            )}
          </div>

          {/* Remarks */}
          {approval.remarks && (
            <div style={{ 
              marginTop: '12px', 
              paddingTop: '12px', 
              borderTop: `1px solid ${Colors.lightGray}` 
            }}>
              <Text style={{ fontSize: '11px', color: Colors.gray, display: 'block', marginBottom: '4px' }}>Remarks</Text>
              <Text style={{ fontSize: '12px', fontStyle: 'italic', color: Colors.textSecondary, lineHeight: '1.4' }}>
                {approval.remarks}
              </Text>
            </div>
          )}

          {/* Update Status Button - Only for pending and if user has can_approve permission */}
          {showUpdateButton && approval.approvalStatus === Constants.APPROVAL_STATUS.PENDING && user?.can_approve && (
            <div style={{ 
              marginTop: '16px', 
              paddingTop: '16px', 
              borderTop: `1px solid ${Colors.lightGray}` 
            }}>
              <CustomButton
                onClick={() => {
                  setSelectedApproval(approval);
                  setUpdateModalOpen(true);
                }}
                variant="filled"
                color={Colors.primary}
                width="100%"
                fontSize={12}
              >
                <EditOutlined /> Update Status
              </CustomButton>
            </div>
          )}
        </Card>
      </Col>
    );
  };

  return (
    <Card>
      {/* Pending Transactions Section */}
      <div style={{ marginBottom: '40px' }}>
        <Title level={5} style={{ marginBottom: '16px', color: Colors.primary }}>Pending Approvals</Title>
        
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
            <Spin />
          </div>
        ) : pendingTransactions.length === 0 ? (
          <div style={{ 
            textAlign: 'center', 
            padding: '40px 20px',
            color: Colors.gray
          }}>
            <Text style={{ fontSize: '16px' }}>No transactions pending for approval</Text>
          </div>
        ) : (
          <Row gutter={[16, 16]}>
            {pendingTransactions.map((approval, index) => renderApprovalCard(approval, index, true))}
          </Row>
        )}
      </div>

      <Divider />

      {/* History Section */}
      <div>
        <Title level={5} style={{ marginBottom: '16px', color: Colors.primary }}>Approval History</Title>
        
        {/* History Filters */}
        <Row gutter={16} style={{ marginBottom: '16px' }}>
          <Col xs={24} sm={12} md={5} style={{ marginBottom: '8px' }}>
            <div style={{ marginBottom: '8px' }}>
              <Text strong>Status</Text>
            </div>
            <CustomSelect
              placeholder="Select status"
              value={historyFilters.status}
              onChange={(value) => setHistoryFilters(prev => ({ ...prev, status: value }))}
              options={[
                { value: '', label: 'All Status' },
                { value: Constants.APPROVAL_STATUS.APPROVED, label: removeUnderScoreAndCapitalize(Constants.APPROVAL_STATUS.APPROVED) },
                { value: Constants.APPROVAL_STATUS.REJECTED, label: removeUnderScoreAndCapitalize(Constants.APPROVAL_STATUS.REJECTED) }
              ]}
            />
          </Col>
          <Col xs={24} sm={12} md={5} style={{ marginBottom: '8px' }}>
            <div style={{ marginBottom: '8px' }}>
              <Text strong>Start Date</Text>
            </div>
            <CustomDatePicker
              placeholder="Start date"
              value={historyFilters.startDate}
              onChange={(date) => setHistoryFilters(prev => ({ ...prev, startDate: date }))}
            />
          </Col>
          <Col xs={24} sm={12} md={5} style={{ marginBottom: '8px' }}>
            <div style={{ marginBottom: '8px' }}>
              <Text strong>End Date</Text>
            </div>
            <CustomDatePicker
              placeholder="End date"
              value={historyFilters.endDate}
              onChange={(date) => setHistoryFilters(prev => ({ ...prev, endDate: date }))}
            />
          </Col>
          <Col xs={24} sm={12} md={3} style={{ marginBottom: '8px' }}>
            <div style={{ marginBottom: '8px' }}>
              <Text strong style={{ opacity: 0 }}>Reset</Text>
            </div>
            <CustomButton
              onClick={() => {
                setHistoryFilters(initialHistoryFilters);
                setHistoryPage(1);
                dispatch(resetHistoryTransactions());
                setHasMoreHistory(true);
              }}
              variant="outline"
              color={Colors.error}
              width="100%"
              fontSize={12}
            >
              Reset Filters
            </CustomButton>
          </Col>
        </Row>

        {historyLoading && historyPage === 1 ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
            <Spin />
          </div>
        ) : historyTransactions.length === 0 ? (
          <div style={{ 
            textAlign: 'center', 
            padding: '40px 20px',
            color: Colors.gray
          }}>
            <Text style={{ fontSize: '16px' }}>No approval history found</Text>
          </div>
        ) : (
          <>
            <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
              {historyTransactions.map((approval, index) => renderApprovalCard(approval, index, false))}
            </Row>
            
            {/* Infinite scroll trigger - Always render for observer */}
            <div 
              ref={observerTarget} 
              style={{ 
                marginTop: '20px', 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center',
                width: '100%'
              }}
            >
              {historyLoading && (
                <Spin />
              )}
            </div>
          </>
        )}
      </div>

      <UpdateApprovalStatusModal
        isOpen={updateModalOpen}
        onClose={() => {
          setUpdateModalOpen(false);
          setSelectedApproval(null);
        }}
        approval={selectedApproval}
      />
    </Card>
  );
};

export default TransactionApprovals;
